// hooks/useUsers.tsx

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  createUser,
  deleteUser,
  getAllActivityLogs,
  getAllUsers,
  getUserById,
  registerUser,
  sendOtp,
  updateUser,
  verifyOtp,
} from '@/services/user/userService';
import { RegisterUserPayload } from '@/types/user';
import { getCurrentUser, syncUserRoleFromData, getUserRole } from '@/lib/auth';

type SendOtpVariables = { phoneNumber: string };
type VerifyOtpVariables = { phoneNumber: string; code: string };
type UpdateUserVariables = { userId: string; userData: any };

// OTP Hooks
export function useSendOtp() {
  return useMutation({
    mutationKey: ['send-otp'],
    mutationFn: async ({ phoneNumber }: SendOtpVariables) =>
      sendOtp(phoneNumber),
  });
}

export function useVerifyOtp() {
  return useMutation({
    mutationKey: ['verify-otp'],
    mutationFn: async ({ phoneNumber, code }: VerifyOtpVariables) =>
      verifyOtp(phoneNumber, code),
  });
}

export function useRegisterUser() {
  return useMutation({
    mutationKey: ['register-user'],
    mutationFn: async (userData: RegisterUserPayload) => registerUser(userData),
  });
}

// User CRUD Hooks

// Get all users (useQuery for GET operations)
export function useGetAllUsers(enabled: boolean = true) {
  return useQuery({
    queryKey: ['users', 'all'],
    queryFn: getAllUsers,
    enabled,
  });
}

// Get user by ID (useQuery for GET operations)
export function useGetUserById(userId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: () => getUserById(userId),
    enabled: enabled && !!userId,
  });
}

// Create user (useMutation for POST operations)
export function useCreateUser() {
  return useMutation({
    mutationKey: ['create-user'],
    mutationFn: async (userData: any) => createUser(userData),
  });
}

// Update user (useMutation for PUT operations)
export function useUpdateUser() {
  return useMutation({
    mutationKey: ['update-user'],
    mutationFn: async ({ userId, userData }: UpdateUserVariables) =>
      updateUser(userId, userData),
  });
}

// Delete user (useMutation for DELETE operations)
export function useDeleteUser() {
  return useMutation({
    mutationKey: ['delete-user'],
    mutationFn: async (userId: string) => deleteUser(userId),
  });
}

// Get all activity logs (useQuery for GET operations)
export function useGetAllActivityLogs(enabled: boolean = true) {
  return useQuery({
    queryKey: ['activity-logs', 'all'],
    queryFn: getAllActivityLogs,
    enabled,
  });
}

// Hook to sync current user role from backend using useGetAllUsers
// This fetches all users, filters by current user's email, and syncs the role
export function useSyncCurrentUserRole(enabled: boolean = true) {
  const { data: usersData, isLoading, refetch } = useGetAllUsers(enabled);
  
  // Sync role whenever users data changes
  useEffect(() => {
    if (!usersData || isLoading) return;
    
    try {
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.email) return;
      
      // Find current user in the users list by email
      const backendUser = usersData.find(
        (user) => user.email === currentUser.email
      );
      
      if (backendUser) {
        const currentRole = getUserRole();
        const backendRole = backendUser.role;
        
        // Sync role from backend user data if it changed
        if (backendRole && backendRole !== currentRole) {
          syncUserRoleFromData(backendUser);
        }
      }
    } catch (error) {
      console.error('Error syncing current user role:', error);
    }
  }, [usersData, isLoading]);
  
  // Refetch users periodically to catch role changes quickly (every 2 seconds)
  useEffect(() => {
    if (!enabled) return;
    
    const interval = setInterval(() => {
      refetch();
    }, 2000); // Refetch every 2 seconds for faster updates
    
    return () => clearInterval(interval);
  }, [enabled, refetch]);
  
  // Refetch users immediately when role change event is detected
  useEffect(() => {
    const handleRoleChange = () => {
      // Immediately refetch to get latest user data
      refetch();
    };
    
    window.addEventListener('userRoleChanged', handleRoleChange);
    
    return () => {
      window.removeEventListener('userRoleChanged', handleRoleChange);
    };
  }, [refetch]);
  
  return { isLoading, refetch };
}
